import React from "react";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  IconButton,
  MenuItem,
  Select,
  TableCell,
  TableRow,
  TextField,
} from "@mui/material";
import { Stack } from "@mui/system";

import { MODEL_OPTIONS } from "../constants";
import {
  SatelliteDeviceField,
  SatelliteDeviceFormValues,
} from "../hooks/use-satellite-devices";

interface AddDeviceRowProps {
  values: SatelliteDeviceFormValues;
  touched: Record<SatelliteDeviceField, boolean>;
  canSubmit: boolean;
  isSubmitting: boolean;
  isEditing: boolean;
  onFieldChange: (field: SatelliteDeviceField, value: string) => void;
  onFieldBlur: (field: SatelliteDeviceField) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const AddDeviceRow = ({
  values,
  touched,
  canSubmit,
  isSubmitting,
  isEditing,
  onFieldChange,
  onFieldBlur,
  onConfirm,
  onCancel,
}: AddDeviceRowProps) => {
  return (
    <TableRow data-testid="satellite-add-device-row">
      <TableCell>
        <TextField
          placeholder="IMEI*"
          variant="outlined"
          size="small"
          fullWidth
          disabled={isEditing}
          error={touched.imei && !values.imei}
          value={values.imei}
          onChange={(e) => onFieldChange("imei", e.target.value)}
          onBlur={() => onFieldBlur("imei")}
          slotProps={{
            htmlInput: {
              maxLength: 50,
              "data-testid": "satellite-add-device-imei",
            },
          }}
        />
      </TableCell>
      <TableCell>
        <Select
          displayEmpty
          variant="outlined"
          size="small"
          fullWidth
          disabled={isEditing}
          error={touched.model && !values.model}
          value={values.model}
          onChange={(e) => onFieldChange("model", e.target.value)}
          onBlur={() => onFieldBlur("model")}
          renderValue={(selected) => (selected ? selected : "Model*")}
          data-testid="satellite-add-device-model"
        >
          {MODEL_OPTIONS.map((model) => (
            <MenuItem key={model.value} value={model.label}>
              {model.label}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <TextField
          placeholder="Notes"
          variant="outlined"
          size="small"
          fullWidth
          value={values.notes}
          onChange={(e) => onFieldChange("notes", e.target.value)}
          slotProps={{
            htmlInput: {
              "data-testid": "satellite-add-device-notes",
            },
          }}
        />
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" alignItems="center" gap={1}>
          <IconButton
            color="success"
            loading={isSubmitting}
            disabled={!canSubmit || isSubmitting}
            onClick={onConfirm}
            aria-label="Confirm add device"
            data-testid="satellite-add-device-confirm"
          >
            <CheckIcon />
          </IconButton>
          <IconButton
            onClick={onCancel}
            disabled={isSubmitting}
            aria-label="Cancel add device"
            data-testid="satellite-add-device-cancel"
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default AddDeviceRow;
